import {
  Button, Checkbox, CircularProgress, Grid, TextField, Typography, FormControlLabel,
} from '@material-ui/core';
import React, { useState } from 'react';

import Link from 'next/link';
import NumberFormat from 'react-number-format';
import ShippingAddressFormFields from 'components/ShippingAddress/ShippingAddressFormFields';
import CardSelector from 'components/Payment/CardSelector';

const CardPaymentForm = (props) => {
  const {
    values: {
      cardNumber, expirationDate, cvv, cardholderName, billingSame, membership, promoCode, addPayment,
    },
    type,
    total,
    buttonText,
    buttonID,
    showMembership,
    showPromoCode,
    handleChange,
    setFieldValue,
    handleSubmit,
    errors,
    submitting,
    message,
    isValid,
  } = props;

  const setBillingField = (val) => {
    setFieldValue('billingSame', val, true);
  };

  // format date for display
  const FormatExpDate = (expDate) => {
    const dateArray = expDate.split(/-/);
    const month = dateArray[1];
    const year = dateArray[0].slice(-2);
    return `${`${month}/${year}`}`;
  };

  const [cardSelected, setCardSelected] = useState('');
  /**
   * HandleCardSelect
   *
   * @param cardIndex - index of card chosen for AuthNet account
   * @param setCard - card details from selection
   *
   * Get thes selected cards information from CardSelector and
   * then populates form fields
   * @param customerProfileId
   */
  const HandleCardSelect = (cardIndex, setCard, customerProfileId) => {
    const card = setCard?.payment?.creditCard;
    const cardHolder = setCard?.billTo;
    // card details
    setFieldValue('cardNumber', `Card ending in ${card?.cardNumber}`, true);
    const expDate = FormatExpDate(card?.expirationDate);
    setFieldValue('expirationDate', expDate, true);
    setFieldValue('customerPaymentProfileId', setCard?.customerPaymentProfileId, true);
    setFieldValue('customerProfileId', customerProfileId);
    // user details
    // check if last name exists to combine for cardholderName
    let name = cardHolder?.firstName;
    if (cardHolder?.lastName !== undefined) {
      name = `${name} ${cardHolder?.lastName}`;
      setFieldValue('lastName', cardHolder.lastName, true);
    }
    setFieldValue('cardholderName', name, true);
    setFieldValue('firstName', cardHolder?.firstName, true);
    setFieldValue('address1', cardHolder.address, true);
    setFieldValue('city', cardHolder.city, true);
    setFieldValue('state', cardHolder.state, true);
    setFieldValue('zipCode', cardHolder.zipCode, true);
    setCardSelected(cardIndex);
  };

  // clears out saved card on card number update
  const clearCard = () => {
    setCardSelected('');
    setFieldValue('customerPaymentProfileId', '', true);
    setFieldValue('customerProfileId', '');
  };

  const handleCardNumber = (e) => {
    clearCard();
    setFieldValue('cardNumber', e.target.value, true);
  };

  return (
    <div className='form-wrapper'>
      {type !== 'payment_update' && type !== 'add_card' && (
        <CardSelector cardSelected={cardSelected} HandleCardSelect={HandleCardSelect} />
      )}
      <form onSubmit={handleSubmit} className='payment-insurance-wrapper'>
        <div className='payment-details'>
          <div className='card-number'>
            <TextField
              id='cardNumber'
              name='cardNumber'
              variant='outlined'
              onChange={handleCardNumber}
              error={Boolean(errors.cardNumber)}
              required
              value={cardNumber}
              margin='normal'
              fullWidth
              label='Card Number'
              placeholder='Card Number'
              x-autocompletetype='cc-number'
            />
          </div>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <NumberFormat
                customInput={TextField}
                format='##/##'
                id='expirationDate'
                allowEmptyFormatting
                mask='_'
                name='expirationDate'
                placeholder='MM/YY'
                label='Expiration Date'
                error={Boolean(errors.expirationDate)}
                onValueChange={(e) => e.value && setFieldValue('expirationDate', e.value)} // use NumberFormat output to give true value for validation
                required
                variant='outlined'
                defaultValue={expirationDate}
                margin='normal'
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                id='cvv'
                name='cvv'
                variant='outlined'
                onChange={handleChange}
                error={Boolean(errors.cvv)}
                required
                value={cvv}
                margin='normal'
                fullWidth
                label='CVV'
                placeholder='CVV'
              />
            </Grid>
          </Grid>
          <TextField
            id='cardholderName'
            name='cardholderName'
            variant='outlined'
            onChange={handleChange}
            error={Boolean(errors.cardholderName)}
            required
            value={cardholderName}
            margin='normal'
            fullWidth
            label='Card Holder Name'
            placeholder='Card Holder Name'
            x-autocompletetype='cc-name'
          />
          { type !== 'add_card' && (
          <FormControlLabel
            margin='normal'
            className='no-bottom-margin'
            control={(
              <Checkbox
                id='addPayment'
                name='addPayment'
                checked={addPayment && !cardSelected}
                onChange={handleChange}
                disabled={cardSelected}
              />
            )}
            label='Save my credit card for future payments'
          />
          )}
          {showPromoCode && (
            <TextField
              id='promoCode'
              name='promoCode'
              variant='outlined'
              onChange={handleChange}
              value={promoCode}
              margin='normal'
              fullWidth
              label='Promo Code'
              placeholder='Promo Code'
            />
          )}
          <Typography variant='body1'>{'Is the billing address the same as shipping address?'}</Typography>
          <div className='form-controls'>
            <Button
              variant={billingSame === 'yes' && billingSame !== '' ? 'contained' : 'outlined'}
              color='primary'
              onClick={() => setBillingField('yes')}
            >
              {'Yes'}
            </Button>
            <Button
              variant={billingSame === 'no' && billingSame !== '' ? 'contained' : 'outlined'}
              color='primary'
              onClick={() => setBillingField('no')}
            >
              {'No'}
            </Button>
          </div>
        </div>
        {billingSame === 'yes' || billingSame === '' ? null : (
          <ShippingAddressFormFields header='Billing Address' {...props} />
        )}
        {type !== 'payment_update' && type !== 'add_card' && (
          <div className='membership-total'>
            <div className='total'>
              <Typography variant='h3'>
                {'Total:'}
                {' '}
                <NumberFormat value={total} displayType='text' thousandSeparator prefix='$' decimalScale={2} fixedDecimalScale />
              </Typography>
            </div>
            {showMembership && (
              <div className='checkBox'>
                <Checkbox
                  checked={membership}
                  name='membership'
                  onChange={handleChange}
                  value={membership}
                  inputProps={{ 'aria-label': 'I&#39;m interested in a Membership Program' }}
                />
                <Typography variant='body1'>
                  {'I'}
                  &#39;
                  {'m interested in a'}
                  {' '}
                  <Link href='/create-order'>
                    <button type='button' className='button-link'>{'Membership Program'}</button>
                  </Link>
                </Typography>
              </div>
            )}
          </div>
        )}
        <div className='form-controls'>
          <Button id={buttonID} variant='contained' color='secondary' type='submit' disabled={!isValid || submitting === true}>
            {submitting === false ? buttonText : <CircularProgress size={20} />}
          </Button>
        </div>
      </form>
      {message.hasMessage && (
        <Typography variant='body1' className='error'>
          {message.message}
        </Typography>
      )}
    </div>
  );
};

export default CardPaymentForm;
